import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import smsAlertService from '../../services/smsAlertService';

const SMSAlertDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('7d');

  useEffect(() => {
    loadStats();
  }, [dateRange]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      const now = new Date();
      const from = new Date();
      
      switch (dateRange) {
        case '24h':
          from.setHours(from.getHours() - 24);
          break;
        case '7d':
          from.setDate(from.getDate() - 7);
          break;
        case '30d':
          from.setDate(from.getDate() - 30);
          break;
        default:
          from.setDate(from.getDate() - 7);
      }

      const data = await smsAlertService?.getSMSStats(null, {
        from: from.toISOString(),
        to: now?.toISOString()
      });

      setStats(data);
    } catch (err) {
      setError(err?.message || 'Failed to load SMS statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-error bg-error/10 border-error';
      case 'high':
        return 'text-warning bg-warning/10 border-warning';
      default:
        return 'text-accent bg-accent/10 border-accent';
    }
  };

  return (
    <>
      <Helmet>
        <title>SMS Alert Dashboard - Grow Guardian</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  SMS Alert Dashboard
                </h1>
                <p className="text-gray-600">
                  Monitor urgent compliance and treatment alerts sent to field teams
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
                <Button onClick={loadStats}>
                  <Icon name="refresh-cw" className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg text-error">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          ) : stats ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Total SMS Sent</h3>
                    <Icon name="message-circle" className="w-5 h-5 text-accent" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total || 0}</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Compliance Alerts</h3>
                    <Icon name="alert-triangle" className="w-5 h-5 text-warning" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.byType?.compliance || 0}</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Treatment Alerts</h3>
                    <Icon name="activity" className="w-5 h-5 text-success" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.byType?.treatment || 0}</p>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Critical Alerts</h3>
                    <Icon name="alert-circle" className="w-5 h-5 text-error" />
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.bySeverity?.critical || 0}</p>
                </div>
              </div>

              {/* Recent Alerts */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-gray-900">Recent SMS Alerts</h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {stats?.recentAlerts?.length > 0 ? (
                    stats?.recentAlerts?.map((alert, index) => (
                      <div key={index} className="p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded border ${
                                  getSeverityColor(alert?.severity)
                                }`}
                              >
                                {alert?.severity?.toUpperCase()}
                              </span>
                              <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                                {alert?.alertType?.replace('_', ' ')?.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-gray-900 font-medium mb-1">{alert?.description}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <Icon name="clock" className="w-4 h-4" />
                                {formatTimestamp(alert?.timestamp)}
                              </span>
                              {alert?.messageSid && (
                                <span className="flex items-center gap-1">
                                  <Icon name="check-circle" className="w-4 h-4 text-success" />
                                  Delivered
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-12 text-center">
                      <Icon name="inbox" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No SMS alerts sent in this period</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Info Panel */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <Icon name="info" className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-2">SMS Alert Configuration</h3>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• SMS alerts are automatically sent for <strong>critical</strong> and <strong>high</strong> severity issues</li>
                      <li>• Field team members must have SMS notifications enabled in their preferences</li>
                      <li>• Compliance violations notify: Field Managers, Compliance Officers, Operations Managers</li>
                      <li>• Treatment alerts notify: Field Managers, IPM Specialists, Operations Managers, and assigned users</li>
                      <li>• All SMS deliveries are logged for audit compliance</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default SMSAlertDashboard;